import { Checkbox, InputLabel, MenuItem, Select, Stack, Typography, styled } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { DatePicker, Input } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { WidgetsData } from '../../WidgetRow';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { ActivityDurationOccurenceUnitType, ActivityUpdateDurationEntityRelationInput } from '../../../../../gql/graphql';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';

export const durationEditDrawerSchema = z.object({
	activityId: z.string(),
});

type RangeValue = [Dayjs | null, Dayjs | null] | null;
const { RangePicker } = DatePicker;

const StyledRangePicker = styled(RangePicker)`
	width: 100%;
	box-shadow: none;
`;

const getActivityDuration = graphql(`
	query durationDrawer_getActivityDuration($activityId: UUID!) {
		getActivity(by: { id: $activityId }) {
			duration {
				start
				end
				type
				showOnActivityMap
				unit
				occurence
			}
		}
	}
`);

const updateDurationMutation = graphql(`
	mutation durationDrawer_updateActivityDuration($activityId: UUID!, $duration: ActivityUpdateDurationEntityRelationInput) {
		updateActivity(by: { id: $activityId }, data: { duration: $duration }) {
			ok
			errorMessage
		}
	}
`);

const updateActivityMapPresence = graphql(`
	mutation durationDrawer_updateActivityMapPresence($activityId: UUID!, $showOnActivityMap: Boolean!) {
		updateActivity(by: { id: $activityId }, data: { duration: { update: { showOnActivityMap: $showOnActivityMap } } }) {
			ok
			errorMessage
		}
	}
`);

type ActivityDurationType = NonNullable<WidgetsData['duration']>['type'];
type WidgetDurationType = ActivityDurationType | '-';
type OccurenceData = Readonly<{
	occurence: number;
	unit: ActivityDurationOccurenceUnitType;
}>;

const getMutationData = (
	type: ActivityDurationType | null,
	updateVariant: 'create' | 'update',
	dates?: RangeValue,
	occurenceData?: OccurenceData,
): ActivityUpdateDurationEntityRelationInput => {
	switch (type) {
		case 'time_limited':
			return {
				[updateVariant]: {
					start: dates?.[0]?.toISOString(),
					end: dates?.[1]?.toISOString(),
					type,
				},
			};
		case 'continuous':
			return {
				[updateVariant]: {
					start: null,
					end: null,
					type,
				},
			};
		case 'one_time':
			return {
				[updateVariant]: {
					start: null,
					end: null,
					type,
				},
			};
		case 'occurence':
			return {
				[updateVariant]: {
					start: null,
					end: null,
					type,
					occurence: occurenceData?.occurence,
					unit: occurenceData?.unit,
				},
			};
		default:
			return {
				delete: true,
			};
	}
};

export const renderValue: Record<WidgetDurationType, ReactElement> = {
	'-': <FormattedMessage id={'journey.detail.drawer.duration.do-not-show'} />,
	time_limited: <FormattedMessage id={'journey.detail.drawer.duration.time-period'} />,
	continuous: <FormattedMessage id={'journey.detail.widget.duration.continuous'} />,
	one_time: <FormattedMessage id={'journey.detail.widget.duration.one-time'} />,
	occurence: <FormattedMessage id={'journey.detail.widget.duration.occurence'} />,
};

const renderUnitValue: Record<ActivityDurationOccurenceUnitType, ReactElement> = {
	day: <FormattedMessage id={'duration.unit.day'} />,
	hour: <FormattedMessage id={'duration.unit.hour'} />,
	month: <FormattedMessage id={'duration.unit.month'} />,
	year: <FormattedMessage id={'duration.unit.year'} />,
};

export const DurationEditDrawer: FC<z.infer<typeof durationEditDrawerSchema>> = ({ activityId }) => {
	const intl = useIntl();
	const { data, isLoading } = useApiQuery(getActivityDuration, { variables: { activityId } });
	const [initDates, setInitDates] = useState<RangeValue>(null);
	const [value, setValue] = useState<RangeValue>(null);
	const { mutate } = useApiMutation(updateDurationMutation);
	const { mutate: mutateActivityMapVisibility } = useApiMutation(updateActivityMapPresence);
	const [occurence, setOccurence] = useState<number | null>(1);
	const [durationType, setDurationType] = useState<WidgetDurationType>('-');
	const { openSnackbar } = useSnackbar();

	useEffect(() => {
		setDurationType(data?.getActivity?.duration?.type ?? '-');
		if (data?.getActivity?.duration?.type === 'time_limited') {
			const initialValues: [Dayjs | null, Dayjs | null] = [dayjs(data.getActivity.duration.start), dayjs(data.getActivity.duration.end)];

			setValue(initialValues.filter(Boolean).length === 0 ? null : initialValues);
			setInitDates(initialValues.filter(Boolean).length === 0 ? null : initialValues);
		} else if (data?.getActivity?.duration?.type === 'occurence') {
			setOccurence(data.getActivity.duration.occurence ?? 1);
		} else {
			setValue(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const updateDuration = useCallback(
		(type: ActivityDurationType | null, dates?: RangeValue, occurenceData?: OccurenceData) => {
			const updateVariant = data?.getActivity?.duration == null ? 'create' : 'update';
			const initialType = data?.getActivity?.duration?.type;
			const initialOccurence = data?.getActivity?.duration?.occurence;
			const initialUnit = data?.getActivity?.duration?.unit;
			const fixedISODates = (dates?.map((date) => dayjs(date).endOf('day')) ?? null) as RangeValue;
			const diffDates =
				initDates?.[0]?.endOf('day').toISOString() !== fixedISODates?.[0]?.toISOString() ||
				initDates?.[1]?.endOf('day').toISOString() !== fixedISODates?.[1]?.toISOString();
			const diffOccurence = initialOccurence !== occurenceData?.occurence;
			const diffUnit = initialUnit !== occurenceData?.unit;
			if (type !== initialType || diffDates || diffOccurence || diffUnit) {
				mutate(
					{
						activityId,
						duration: getMutationData(type, updateVariant, fixedISODates, occurenceData),
					},
					{
						onSuccess: (result) =>
							mutationResponseHandler({
								result: result.updateActivity.ok,
								toastMessages: { error: intl.formatMessage({ id: 'widget.duration.update.error' }) },
								errorMessage: result.updateActivity.errorMessage,
								openSnackbar,
							}),
					},
				);
			}
		},
		[data?.getActivity?.duration, initDates, mutate, activityId, intl, openSnackbar],
	);

	useDebounceEffect(() => {
		const initialOccurence = data?.getActivity?.duration?.occurence;
		const diffOccurence = initialOccurence !== occurence;
		if (durationType === 'occurence' && diffOccurence) {
			updateDuration('occurence', undefined, { occurence: occurence ?? 0, unit: data?.getActivity?.duration?.unit ?? 'day' });
		}
	}, [occurence, durationType]);

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.detail.widget.duration.title' })} />
			<Select
				name={'duration'}
				fullWidth
				value={durationType}
				renderValue={(value) => renderValue[value]}
				onChange={({ target: { value } }) => {
					const durationType = value as WidgetDurationType;
					setDurationType(durationType);

					if (durationType === 'continuous') {
						updateDuration('continuous');
						return;
					}
					if (durationType === 'one_time') {
						updateDuration('one_time');
						return;
					}
					if (durationType === '-') {
						updateDuration(null);
						return;
					}
				}}
			>
				{Object.entries(renderValue).map(([key, value]) => (
					<MenuItem key={key} value={key}>
						{value}
					</MenuItem>
				))}
			</Select>
			{durationType === 'time_limited' && (
				<Stack
					direction={'row'}
					flexWrap={'nowrap'}
					width={'100%'}
					alignItems={'center'}
					sx={{
						border: (theme) => `1px solid ${theme.palette.day[300]}`,
						px: 1.5,
						borderRadius: 1,
						paddingRight: 0,
					}}
				>
					<CalendarTodayOutlined color={'secondary'} />
					<StyledRangePicker
						suffixIcon={false}
						value={value}
						size={'large'}
						bordered={false}
						allowEmpty={[false, true]}
						onChange={(val) => {
							setValue(val);
							if (val === null) {
								updateDuration(null);
								return;
							}
							if (val[1] === null) {
								setValue([val[0], val[0]]);
								updateDuration('time_limited', [val[0], val[0]]);
								return;
							}
							updateDuration('time_limited', val);
						}}
					/>
				</Stack>
			)}
			{durationType === 'occurence' && (
				<Stack gap={2} flexWrap={'nowrap'} width={'100%'} alignItems={'flex-start'}>
					<Typography variant={'h4'}>
						<FormattedMessage id={'duration.occurence.title'} />
					</Typography>
					<Stack flexDirection={'row'} gap={1.5}>
						<Input
							type={'number'}
							value={occurence ?? 0}
							min={1}
							onChange={({ currentTarget: { valueAsNumber } }) => setOccurence(valueAsNumber)}
						/>
						<Select
							name={'duration'}
							value={data?.getActivity?.duration?.unit ?? 'day'}
							renderValue={(value) => renderUnitValue[value]}
							onChange={({ target: { value } }) => {
								const unit = value as ActivityDurationOccurenceUnitType;
								updateDuration('occurence', undefined, { occurence: occurence ?? 0, unit });
							}}
						>
							<MenuItem value={'hour'}>
								<FormattedMessage id={'duration.unit.hour'} />
							</MenuItem>
							<MenuItem value={'day'}>
								<FormattedMessage id={'duration.unit.day'} />
							</MenuItem>
							<MenuItem value={'month'}>
								<FormattedMessage id={'duration.unit.month'} />
							</MenuItem>
							<MenuItem value={'year'}>
								<FormattedMessage id={'duration.unit.year'} />
							</MenuItem>
						</Select>
					</Stack>
				</Stack>
			)}
			<InputLabel>
				<Checkbox
					checked={data?.getActivity?.duration?.showOnActivityMap}
					onChange={() =>
						mutateActivityMapVisibility({ activityId, showOnActivityMap: !Boolean(data?.getActivity?.duration?.showOnActivityMap) })
					}
				/>
				<FormattedMessage id={'journey.detail.drawer.duration.show-activity-map'} />
			</InputLabel>
		</DrawerWrapper>
	);
};
